import React, { useEffect, useRef, useState } from "react";

interface OptionType {
  value: number | string;
  label: string;
}

interface CurrencyRateInputProps {
  currencyValue?: number | string | null;
  currencyOptions: OptionType[];
  rate: string;
  onCurrencyChange: (option?: OptionType) => void;
  onRateChange: (value: string) => void;
  disabled?: boolean;
  currencyDisabled?: boolean;
}

const CurrencyRateInput: React.FC<CurrencyRateInputProps> = ({
  currencyValue,
  currencyOptions,
  rate,
  onCurrencyChange,
  onRateChange,
  disabled = false,
  currencyDisabled = false,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const selectedOption = currencyOptions.find(
    (o) => String(o.value) === String(currencyValue),
  );

  const openDropdown = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();

    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      maxHeight: "220px",
      overflowY: "auto",
      background: "#fff",
      border: "1px solid #dee2e6",
      borderRadius: "4px",
    });

    setOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current?.contains(e.target as Node) ||
        triggerRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };

    window.addEventListener("scroll", handleScroll, true); // capture mode
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, []);

  return (
    <div
      className="d-flex align-items-center"
      style={{
        border: "1px solid #ced4da",
        borderRadius: "4px",
        overflow: "hidden",
        height: "31px",
        width: "180px",
        background: disabled ? "#e9ecef" : "#fff",
      }}
    >
      {/* Currency Trigger */}
      {/* Currency Trigger */}
      <div
        ref={triggerRef}
        className="form-control form-control-sm border-0 position-relative"
        style={{
          width: "90px",
          cursor: disabled ? "not-allowed" : "pointer",
          backgroundColor: "transparent",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          paddingRight: "22px", // space for arrow
        }}
        onClick={() => !disabled && !currencyDisabled && openDropdown()}
        title={selectedOption?.label}
      >
        {selectedOption?.label || "--"}

        {/* Arrow Icon */}
        {!currencyDisabled && (
          <div
            className="material-icons material-symbols-outlined multiselect_iconSearchArroe"
            style={{
              position: "absolute",
              right: "4px",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "18px",
              pointerEvents: "none",
            }}
          >
            keyboard_arrow_down
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{ width: "1px", background: "#dee2e6", height: "100%" }} />

      {/* Rate Input */}
      <input
        type="text"
        className="form-control form-control-sm border-0 text-end"
        style={{ boxShadow: "none" }}
        value={rate}
        onChange={(e) => {
          const value = e.target.value;
          const regex = /^\d{0,11}(\.\d{0,2})?$/;

          if (value === "" || regex.test(value)) {
            onRateChange(value);
          }
        }}
        disabled={disabled || !selectedOption}
      />

      {/* Styled Dropdown List */}
      {open && (
        <ul ref={dropdownRef} className="list-group" style={dropdownStyle}>
          {currencyOptions.map((opt) => {
            const isSelected = String(opt.value) === String(currencyValue);

            return (
              <li
                key={opt.value}
                className="list-group-item"
                style={{
                  cursor: "pointer",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "5px 10px",
                  backgroundColor: isSelected ? "#e9f3ff" : "#fff",
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onCurrencyChange(opt);
                  setOpen(false);
                }}
              >
                <input type="radio" checked={isSelected} readOnly />
                <span>{opt.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CurrencyRateInput;
